/**
 * FinanzPruefungPage — Prüfung im Finanzen-Cockpit (Sektion „Prüfung",
 * Route /finanzen/:firma/pruefung)
 *
 * Eingebettet im Mandanten-Cockpit. Dient der Vollständigkeitskontrolle und
 * liefert das Buchungsjournal für die Gegenprüfung durch den Steuerberater
 * (Nils). Ruhig gehalten (slate/zinc), ein einziger emerald-Akzent.
 *
 * Prop `firmaKey`:
 *   'sv'   -> SV Büro   (firma = 'sv_buero_koop')
 *   'lcs'  -> LCS        (firma = 'lcs')
 *   'alle' -> alle Firmen (keine Firma-Einschränkung)
 *
 * DATENQUELLEN (read-only Views, bereits vorhanden — keine Migration):
 *   - v_pruef_ausgaben_ohne_beleg: id, firma, buchung_datum, betrag, rohtext, status
 *   - v_pruef_belege_ohne_buchung: id, firma, belegdatum, absender_firma,
 *                                  brutto_betrag, status, zahlweg
 *   - v_buchungsjournal:           id, firma, marke, buchung_datum, betrag, netto,
 *                                  steuer, steuersatz, konto_nr, konto_bez,
 *                                  gegenkonto, buchungstext, beleg_vorhanden,
 *                                  status, quelle, created_at
 *
 * RLS/Policies bleiben unangetastet (Inhaber-only).
 * Alle Abfragen sind robust gegen fehlende Views/leere Daten (kein Absturz).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  ClipboardCheck, Loader2, Inbox, CheckCircle2, AlertTriangle, Download,
  FileWarning, ReceiptText, BookOpen,
} from 'lucide-react';

// ============================================================
// Firmen-Mapping
// ============================================================

export type PruefungFirmaKey = 'sv' | 'lcs' | 'alle';

/** firmaKey -> konkreter firma-String (für Filter). 'alle' = keine Einschränkung. */
const FIRMA_STRING: Record<Exclude<PruefungFirmaKey, 'alle'>, string> = {
  sv: 'sv_buero_koop',
  lcs: 'lcs',
};

const FIRMA_LABEL: Record<string, string> = {
  sv_buero_koop: 'SV Büro',
  lcs: 'LCS',
  prolea_fzco: 'Prolea FZCO',
  pv_gbr: 'PV GbR',
};

// ============================================================
// Typen
// ============================================================

interface AusgabeOhneBeleg {
  id: string | number;
  firma: string | null;
  buchung_datum: string | null;
  betrag: number | string | null;
  rohtext: string | null;
  status: string | null;
}

interface BelegOhneBuchung {
  id: string | number;
  firma: string | null;
  belegdatum: string | null;
  absender_firma: string | null;
  brutto_betrag: number | string | null;
  status: string | null;
  zahlweg: string | null;
}

interface JournalRow {
  id: string | number;
  firma: string | null;
  marke: string | null;
  buchung_datum: string | null;
  betrag: number | string | null;
  netto: number | string | null;
  steuer: number | string | null;
  steuersatz: number | string | null;
  konto_nr: string | null;
  konto_bez: string | null;
  gegenkonto: string | null;
  buchungstext: string | null;
  beleg_vorhanden: boolean | null;
  status: string | null;
  quelle: string | null;
  created_at: string | null;
}

type TabId = 'uebersicht' | 'offene_punkte' | 'journal';

// ============================================================
// Helpers
// ============================================================

function isViewNotFound(error: unknown): boolean {
  if (!error) return false;
  const e = error as { message?: string; code?: string };
  const msg = (e.message || '') + ' ' + (e.code || '');
  return (
    msg.includes('PGRST205') ||
    msg.includes('schema cache') ||
    msg.includes('relation') ||
    msg.includes('does not exist')
  );
}

function toNum(v: number | string | null | undefined): number {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtEuro(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function fmtDatum(d: string | null): string {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Bereinigt den Bank-Rohtext (Doppel-Leerzeichen entfernen). */
function bereinigeRohtext(s: string | null): string {
  if (!s) return '';
  return s.replace(/\s+/g, ' ').trim();
}

const MONATE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

/** Erzeugt aus einem ISO/Datum-String den Monatsschlüssel YYYY-MM (oder ''). */
function monatsKey(d: string | null): string {
  if (!d || d.length < 7) return '';
  return d.slice(0, 7);
}

// ============================================================
// Daten-Layer
// ============================================================

async function ladeAusgabenOhneBeleg(firmaKey: PruefungFirmaKey): Promise<AusgabeOhneBeleg[]> {
  try {
    let query = supabase
      .from('v_pruef_ausgaben_ohne_beleg')
      .select('id, firma, buchung_datum, betrag, rohtext, status')
      .limit(10000);
    if (firmaKey !== 'alle') {
      query = query.eq('firma', FIRMA_STRING[firmaKey]);
    }
    const { data, error } = await query;
    if (error) throw error;
    return (data as AusgabeOhneBeleg[]) || [];
  } catch (err) {
    if (!isViewNotFound(err)) console.error('Prüfung: Ausgaben ohne Beleg laden fehlgeschlagen:', err);
    return [];
  }
}

async function ladeBelegeOhneBuchung(firmaKey: PruefungFirmaKey): Promise<BelegOhneBuchung[]> {
  try {
    let query = supabase
      .from('v_pruef_belege_ohne_buchung')
      .select('id, firma, belegdatum, absender_firma, brutto_betrag, status, zahlweg')
      .limit(10000);
    if (firmaKey !== 'alle') {
      query = query.eq('firma', FIRMA_STRING[firmaKey]);
    }
    const { data, error } = await query;
    if (error) throw error;
    return (data as BelegOhneBuchung[]) || [];
  } catch (err) {
    if (!isViewNotFound(err)) console.error('Prüfung: Belege ohne Buchung laden fehlgeschlagen:', err);
    return [];
  }
}

async function ladeJournal(firmaKey: PruefungFirmaKey): Promise<JournalRow[]> {
  try {
    let query = supabase
      .from('v_buchungsjournal')
      .select(
        'id, firma, marke, buchung_datum, betrag, netto, steuer, steuersatz, konto_nr, konto_bez, gegenkonto, buchungstext, beleg_vorhanden, status, quelle, created_at',
      )
      .limit(50000);
    if (firmaKey !== 'alle') {
      query = query.eq('firma', FIRMA_STRING[firmaKey]);
    }
    const { data, error } = await query;
    if (error) throw error;
    return (data as JournalRow[]) || [];
  } catch (err) {
    if (!isViewNotFound(err)) console.error('Prüfung: Buchungsjournal laden fehlgeschlagen:', err);
    return [];
  }
}

// ============================================================
// CSV-Export (clientseitig, Semikolon, de-DE)
// ============================================================

/** Maskiert einen Wert für eine Semikolon-CSV (de-DE, Excel-kompatibel). */
function csvFeld(v: string | number | boolean | null | undefined): string {
  const s = v == null ? '' : String(v);
  if (/[";\n\r]/.test(s)) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

/** Zahl im deutschen Format (Komma als Dezimaltrenner) für die CSV. */
function csvZahl(v: number | string | null | undefined): string {
  const n = toNum(v);
  return n.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  });
}

function exportiereJournalCsv(rows: JournalRow[], dateiname: string) {
  const kopf = [
    'Datum', 'Konto-Nr', 'Konto-Bezeichnung', 'Gegenkonto', 'Buchungstext',
    'Netto', 'Steuer', 'Steuersatz', 'Brutto', 'Beleg', 'Status', 'Quelle', 'Firma',
  ];
  const zeilen = rows.map((r) =>
    [
      csvFeld(fmtDatum(r.buchung_datum)),
      csvFeld(r.konto_nr),
      csvFeld(r.konto_bez),
      csvFeld(r.gegenkonto),
      csvFeld(r.buchungstext),
      csvFeld(csvZahl(r.netto)),
      csvFeld(csvZahl(r.steuer)),
      csvFeld(r.steuersatz != null && r.steuersatz !== '' ? `${csvZahl(r.steuersatz)}%` : ''),
      csvFeld(csvZahl(r.betrag)),
      csvFeld(r.beleg_vorhanden ? 'ja' : 'nein'),
      csvFeld(r.status),
      csvFeld(r.quelle),
      csvFeld(r.firma && FIRMA_LABEL[r.firma] ? FIRMA_LABEL[r.firma] : r.firma),
    ].join(';'),
  );
  // BOM voranstellen, damit Excel UTF-8 korrekt erkennt.
  const inhalt = '﻿' + [kopf.join(';'), ...zeilen].join('\r\n');
  const blob = new Blob([inhalt], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = dateiname;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ============================================================
// UI-Bausteine
// ============================================================

/** Dezentes Badge (slate). */
function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
      {children}
    </span>
  );
}

/** Tab-Umschalter. */
function TabButton({
  aktiv, onClick, icon, children,
}: {
  aktiv: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold border transition-colors ${
        aktiv
          ? 'bg-emerald-600 text-white border-emerald-600'
          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

/**
 * Ampel-Karte: grün (CheckCircle) wenn keine offenen Punkte, sonst dezent rose.
 * Neutrale Karten (kein Ampelbezug) bleiben slate.
 */
function AmpelKarte({
  titel, wert, zusatz, zustand,
}: {
  titel: string;
  wert: string;
  zusatz?: string;
  zustand: 'gut' | 'offen' | 'neutral';
}) {
  const styles =
    zustand === 'gut'
      ? 'bg-emerald-50 border-emerald-200'
      : zustand === 'offen'
        ? 'bg-rose-50 border-rose-200'
        : 'bg-white border-slate-200';
  const wertFarbe =
    zustand === 'gut'
      ? 'text-emerald-700'
      : zustand === 'offen'
        ? 'text-rose-700'
        : 'text-slate-900';
  return (
    <div className={`rounded-xl p-4 border ${styles}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{titel}</div>
        {zustand === 'gut' && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
        {zustand === 'offen' && <AlertTriangle className="w-4 h-4 text-rose-500" />}
      </div>
      <div className={`text-2xl font-bold mt-1 tabular-nums ${wertFarbe}`}>{wert}</div>
      {zusatz && <div className="text-xs text-slate-400 mt-0.5">{zusatz}</div>}
    </div>
  );
}

/** Leerer-Zustand-Block. */
function LeerBlock({ titel, text }: { titel: string; text: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
      <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center mx-auto mb-4">
        <Inbox className="w-6 h-6 text-slate-300" />
      </div>
      <h3 className="text-base font-bold text-slate-900">{titel}</h3>
      <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto leading-relaxed">{text}</p>
    </div>
  );
}

// ============================================================
// Abschnitt A — Übersicht / Ampel
// ============================================================

function UebersichtTab({
  ausgaben, belege, journal,
}: {
  ausgaben: AusgabeOhneBeleg[];
  belege: BelegOhneBuchung[];
  journal: JournalRow[];
}) {
  const ausgabenSumme = useMemo(
    () => ausgaben.reduce((s, a) => s + Math.abs(toNum(a.betrag)), 0),
    [ausgaben],
  );
  const journalSumme = useMemo(
    () => journal.reduce((s, j) => s + Math.abs(toNum(j.betrag)), 0),
    [journal],
  );
  const belegMitBuchung = useMemo(
    () => journal.filter((j) => j.beleg_vorhanden === true).length,
    [journal],
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <AmpelKarte
          titel="Ausgaben ohne Beleg"
          wert={String(ausgaben.length)}
          zusatz={ausgaben.length > 0 ? `Summe ${fmtEuro(ausgabenSumme)}` : 'Alles belegt'}
          zustand={ausgaben.length === 0 ? 'gut' : 'offen'}
        />
        <AmpelKarte
          titel="Belege ohne Buchung"
          wert={String(belege.length)}
          zusatz={belege.length > 0 ? 'Noch zu verbuchen' : 'Alles verbucht'}
          zustand={belege.length === 0 ? 'gut' : 'offen'}
        />
        <AmpelKarte
          titel="Buchungen gesamt"
          wert={String(journal.length)}
          zusatz={`Volumen ${fmtEuro(journalSumme)}`}
          zustand="neutral"
        />
      </div>

      {/* Gesamtstatus-Banner */}
      {ausgaben.length === 0 && belege.length === 0 ? (
        <div className="flex items-start gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl p-3.5">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-emerald-700 leading-relaxed">
            Keine offenen Punkte — alle Bank-Ausgaben sind belegt und alle Belege
            verbucht. Das Buchungsjournal kann an den Steuerberater übergeben werden.
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2.5 bg-rose-50 border border-rose-200 rounded-xl p-3.5">
          <AlertTriangle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-rose-700 leading-relaxed">
            {ausgaben.length > 0 && (
              <span>{ausgaben.length} Bank-Ausgabe(n) ohne Beleg. </span>
            )}
            {belege.length > 0 && (
              <span>{belege.length} Beleg(e) noch nicht verbucht. </span>
            )}
            Bitte unter „Offene Punkte" prüfen, bevor an den Steuerberater übergeben wird.
          </div>
        </div>
      )}

      {/* Kleine Zusatzkennzahl */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Buchungen mit Beleg
        </div>
        <div className="text-xl font-bold text-slate-900 mt-1 tabular-nums">
          {belegMitBuchung} <span className="text-sm font-normal text-slate-400">von {journal.length}</span>
        </div>
        <div className="text-xs text-slate-400 mt-0.5">
          {journal.length > 0
            ? `${Math.round((belegMitBuchung / journal.length) * 100)} % der Buchungen haben einen Beleg`
            : 'Noch keine Buchungen vorhanden'}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Abschnitt B — Offene Punkte
// ============================================================

const SEITEN_GROESSE = 50;

function OffenePunkteTab({
  ausgaben, belege,
}: {
  ausgaben: AusgabeOhneBeleg[];
  belege: BelegOhneBuchung[];
}) {
  const [ausgabenLimit, setAusgabenLimit] = useState(SEITEN_GROESSE);
  const [belegeLimit, setBelegeLimit] = useState(SEITEN_GROESSE);

  const ausgabenSortiert = useMemo(
    () =>
      ausgaben.slice().sort((a, b) => {
        const da = a.buchung_datum || '';
        const db = b.buchung_datum || '';
        return da < db ? 1 : da > db ? -1 : 0;
      }),
    [ausgaben],
  );
  const belegeSortiert = useMemo(
    () =>
      belege.slice().sort((a, b) => {
        const da = a.belegdatum || '';
        const db = b.belegdatum || '';
        return da < db ? 1 : da > db ? -1 : 0;
      }),
    [belege],
  );

  return (
    <div className="space-y-6">
      {/* (1) Bank-Ausgaben ohne Beleg */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2.5">
          <FileWarning className="w-4 h-4 text-slate-500" />
          <div>
            <h3 className="text-sm font-bold text-slate-900">Bank-Ausgaben ohne Beleg</h3>
            <p className="text-xs text-slate-400">{ausgaben.length} Einträge — Beleg fehlt oder nicht zugeordnet</p>
          </div>
        </div>
        {ausgabenSortiert.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">
            Keine offenen Ausgaben — alles belegt.
          </div>
        ) : (
          <>
            <div className="max-h-[480px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 w-28">Datum</th>
                    <th className="text-right font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 w-32">Betrag</th>
                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5">Verwendungszweck</th>
                  </tr>
                </thead>
                <tbody>
                  {ausgabenSortiert.slice(0, ausgabenLimit).map((a) => (
                    <tr key={String(a.id)} className="border-t border-slate-50">
                      <td className="px-4 py-2.5 text-slate-500 tabular-nums whitespace-nowrap">{fmtDatum(a.buchung_datum)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-medium text-rose-700 whitespace-nowrap">
                        {fmtEuro(Math.abs(toNum(a.betrag)))}
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">
                        <span className="line-clamp-2">{bereinigeRohtext(a.rohtext) || '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {ausgabenLimit < ausgabenSortiert.length && (
              <div className="px-4 py-3 border-t border-slate-100 text-center">
                <button
                  type="button"
                  onClick={() => setAusgabenLimit((l) => l + SEITEN_GROESSE)}
                  className="px-3.5 py-2 rounded-lg text-sm font-semibold bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                >
                  Weitere {Math.min(SEITEN_GROESSE, ausgabenSortiert.length - ausgabenLimit)} anzeigen
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* (2) Belege ohne Buchung */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2.5">
          <ReceiptText className="w-4 h-4 text-slate-500" />
          <div>
            <h3 className="text-sm font-bold text-slate-900">Belege ohne Buchung</h3>
            <p className="text-xs text-slate-400">{belege.length} Einträge — Beleg vorhanden, aber noch nicht verbucht</p>
          </div>
        </div>
        {belegeSortiert.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">
            Keine offenen Belege — alles verbucht.
          </div>
        ) : (
          <>
            <div className="max-h-[480px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 w-28">Datum</th>
                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5">Absender</th>
                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 w-28">Zahlweg</th>
                    <th className="text-right font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 w-32">Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  {belegeSortiert.slice(0, belegeLimit).map((b) => (
                    <tr key={String(b.id)} className="border-t border-slate-50">
                      <td className="px-4 py-2.5 text-slate-500 tabular-nums whitespace-nowrap">{fmtDatum(b.belegdatum)}</td>
                      <td className="px-4 py-2.5 text-slate-700">{b.absender_firma || '—'}</td>
                      <td className="px-4 py-2.5 text-slate-500">{b.zahlweg || '—'}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-medium text-slate-800 whitespace-nowrap">
                        {fmtEuro(Math.abs(toNum(b.brutto_betrag)))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {belegeLimit < belegeSortiert.length && (
              <div className="px-4 py-3 border-t border-slate-100 text-center">
                <button
                  type="button"
                  onClick={() => setBelegeLimit((l) => l + SEITEN_GROESSE)}
                  className="px-3.5 py-2 rounded-lg text-sm font-semibold bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                >
                  Weitere {Math.min(SEITEN_GROESSE, belegeSortiert.length - belegeLimit)} anzeigen
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Abschnitt C — Buchungsjournal (für Steuerberater)
// ============================================================

function JournalTab({
  journal, firmaKey,
}: {
  journal: JournalRow[];
  firmaKey: PruefungFirmaKey;
}) {
  const [monatFilter, setMonatFilter] = useState<string>('alle');
  const [firmaFilter, setFirmaFilter] = useState<string>('alle');
  const [limit, setLimit] = useState(SEITEN_GROESSE);

  // Verfügbare Monate aus den Daten (absteigend).
  const monatsOptionen = useMemo(() => {
    const set = new Set<string>();
    for (const j of journal) {
      const k = monatsKey(j.buchung_datum);
      if (k) set.add(k);
    }
    return Array.from(set).sort((a, b) => (a < b ? 1 : -1));
  }, [journal]);

  // Verfügbare Firmen aus den Daten (nur relevant bei firmaKey === 'alle').
  const firmaOptionen = useMemo(() => {
    const set = new Set<string>();
    for (const j of journal) {
      if (j.firma) set.add(j.firma);
    }
    return Array.from(set).sort();
  }, [journal]);

  const gefiltert = useMemo(() => {
    const rows = journal.filter((j) => {
      if (monatFilter !== 'alle' && monatsKey(j.buchung_datum) !== monatFilter) return false;
      if (firmaFilter !== 'alle' && j.firma !== firmaFilter) return false;
      return true;
    });
    return rows.sort((a, b) => {
      const da = a.buchung_datum || '';
      const db = b.buchung_datum || '';
      if (da !== db) return da < db ? -1 : 1;
      const ca = a.created_at || '';
      const cb = b.created_at || '';
      return ca < cb ? -1 : ca > cb ? 1 : 0;
    });
  }, [journal, monatFilter, firmaFilter]);

  const summeNetto = useMemo(() => gefiltert.reduce((s, j) => s + toNum(j.netto), 0), [gefiltert]);
  const summeSteuer = useMemo(() => gefiltert.reduce((s, j) => s + toNum(j.steuer), 0), [gefiltert]);
  const summeBrutto = useMemo(() => gefiltert.reduce((s, j) => s + toNum(j.betrag), 0), [gefiltert]);

  const exportieren = () => {
    const teilMonat = monatFilter !== 'alle' ? `_${monatFilter}` : '';
    const teilFirma = firmaKey !== 'alle' ? `_${firmaKey}` : firmaFilter !== 'alle' ? `_${firmaFilter}` : '';
    exportiereJournalCsv(gefiltert, `buchungsjournal${teilFirma}${teilMonat}.csv`);
  };

  function monatLabel(k: string): string {
    const m = Number(k.slice(5, 7));
    const y = k.slice(0, 4);
    return m >= 1 && m <= 12 ? `${MONATE[m - 1]} ${y}` : k;
  }

  return (
    <div className="space-y-4">
      {/* Filter + Export */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={monatFilter}
            onChange={(e) => { setMonatFilter(e.target.value); setLimit(SEITEN_GROESSE); }}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
          >
            <option value="alle">Alle Monate</option>
            {monatsOptionen.map((k) => (
              <option key={k} value={k}>{monatLabel(k)}</option>
            ))}
          </select>
          {firmaKey === 'alle' && firmaOptionen.length > 1 && (
            <select
              value={firmaFilter}
              onChange={(e) => { setFirmaFilter(e.target.value); setLimit(SEITEN_GROESSE); }}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
            >
              <option value="alle">Alle Firmen</option>
              {firmaOptionen.map((f) => (
                <option key={f} value={f}>{FIRMA_LABEL[f] || f}</option>
              ))}
            </select>
          )}
          <span className="text-xs text-slate-400">{gefiltert.length} Buchungen</span>
        </div>
        <button
          type="button"
          onClick={exportieren}
          disabled={gefiltert.length === 0}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          Als CSV exportieren
        </button>
      </div>

      {/* Summen */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-3.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Netto</div>
          <div className="text-base font-bold text-slate-900 mt-0.5 tabular-nums">{fmtEuro(summeNetto)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Steuer</div>
          <div className="text-base font-bold text-slate-900 mt-0.5 tabular-nums">{fmtEuro(summeSteuer)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Brutto</div>
          <div className="text-base font-bold text-slate-900 mt-0.5 tabular-nums">{fmtEuro(summeBrutto)}</div>
        </div>
      </div>

      {/* Tabelle */}
      {gefiltert.length === 0 ? (
        <LeerBlock
          titel="Keine Buchungen"
          text="Für die gewählten Filter liegen keine Buchungen vor. Passe Monat oder Firma an."
        />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 border-b border-slate-100 z-10">
                  <tr>
                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-3 py-2.5 whitespace-nowrap">Datum</th>
                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-3 py-2.5 whitespace-nowrap">Konto</th>
                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-3 py-2.5 whitespace-nowrap">Gegenkonto</th>
                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-3 py-2.5">Buchungstext</th>
                    <th className="text-right font-semibold text-xs uppercase tracking-wide text-slate-500 px-3 py-2.5 whitespace-nowrap">Netto</th>
                    <th className="text-right font-semibold text-xs uppercase tracking-wide text-slate-500 px-3 py-2.5 whitespace-nowrap">Steuer</th>
                    <th className="text-right font-semibold text-xs uppercase tracking-wide text-slate-500 px-3 py-2.5 whitespace-nowrap">Brutto</th>
                    <th className="text-center font-semibold text-xs uppercase tracking-wide text-slate-500 px-3 py-2.5 whitespace-nowrap">Beleg</th>
                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-3 py-2.5 whitespace-nowrap">Status</th>
                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-3 py-2.5 whitespace-nowrap">Quelle</th>
                  </tr>
                </thead>
                <tbody>
                  {gefiltert.slice(0, limit).map((j) => (
                    <tr key={String(j.id)} className="border-t border-slate-50 hover:bg-slate-50/50">
                      <td className="px-3 py-2.5 text-slate-500 tabular-nums whitespace-nowrap">{fmtDatum(j.buchung_datum)}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className="text-slate-700 font-medium">{j.konto_nr || '—'}</span>
                        {j.konto_bez && <span className="text-slate-400 ml-1.5 text-xs">{j.konto_bez}</span>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-500 tabular-nums whitespace-nowrap">{j.gegenkonto || '—'}</td>
                      <td className="px-3 py-2.5 text-slate-600 min-w-[200px]">
                        <span className="line-clamp-2">{j.buchungstext || '—'}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{fmtEuro(toNum(j.netto))}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-500 whitespace-nowrap">{fmtEuro(toNum(j.steuer))}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-medium text-slate-900 whitespace-nowrap">{fmtEuro(toNum(j.betrag))}</td>
                      <td className="px-3 py-2.5 text-center whitespace-nowrap">
                        {j.beleg_vorhanden ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700">
                            <CheckCircle2 className="w-3.5 h-3.5" /> ja
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-500">
                            <AlertTriangle className="w-3.5 h-3.5" /> nein
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {j.status ? <Badge>{j.status}</Badge> : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-400 text-xs whitespace-nowrap">{j.quelle || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {limit < gefiltert.length && (
            <div className="px-4 py-3 border-t border-slate-100 text-center">
              <button
                type="button"
                onClick={() => setLimit((l) => l + SEITEN_GROESSE)}
                className="px-3.5 py-2 rounded-lg text-sm font-semibold bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              >
                Weitere {Math.min(SEITEN_GROESSE, gefiltert.length - limit)} anzeigen
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex items-start gap-2 text-xs text-slate-400">
        <BookOpen className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>
          Vollständiges Buchungsjournal zur Gegenprüfung. Der CSV-Export (Semikolon-getrennt,
          de-DE) berücksichtigt die aktuellen Filter und ist für die Weitergabe an den
          Steuerberater bestimmt.
        </span>
      </div>
    </div>
  );
}

// ============================================================
// Hauptkomponente
// ============================================================

export default function FinanzPruefungPage({ firmaKey = 'sv' }: { firmaKey?: PruefungFirmaKey }) {
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabId>('uebersicht');
  const [ausgaben, setAusgaben] = useState<AusgabeOhneBeleg[]>([]);
  const [belege, setBelege] = useState<BelegOhneBuchung[]>([]);
  const [journal, setJournal] = useState<JournalRow[]>([]);

  const lade = useCallback(async () => {
    setLoading(true);
    const [a, b, j] = await Promise.all([
      ladeAusgabenOhneBeleg(firmaKey),
      ladeBelegeOhneBuchung(firmaKey),
      ladeJournal(firmaKey),
    ]);
    setAusgaben(a);
    setBelege(b);
    setJournal(j);
    setLoading(false);
  }, [firmaKey]);

  useEffect(() => {
    lade();
  }, [lade]);

  const offeneGesamt = ausgaben.length + belege.length;

  return (
    <div className="space-y-5">
      {/* Kopf */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center">
            <ClipboardCheck className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Prüfung</h2>
            <p className="text-xs text-slate-400">Vollständigkeitskontrolle &amp; Buchungsjournal für den Steuerberater</p>
          </div>
        </div>
        {!loading && (
          <div className="text-xs text-slate-400">
            {offeneGesamt === 0
              ? 'Keine offenen Punkte'
              : `${offeneGesamt} offene(r) Punkt(e)`}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <TabButton
          aktiv={tab === 'uebersicht'}
          onClick={() => setTab('uebersicht')}
          icon={<ClipboardCheck className="w-4 h-4" />}
        >
          Übersicht / Ampel
        </TabButton>
        <TabButton
          aktiv={tab === 'offene_punkte'}
          onClick={() => setTab('offene_punkte')}
          icon={<AlertTriangle className="w-4 h-4" />}
        >
          Offene Punkte{offeneGesamt > 0 ? ` (${offeneGesamt})` : ''}
        </TabButton>
        <TabButton
          aktiv={tab === 'journal'}
          onClick={() => setTab('journal')}
          icon={<BookOpen className="w-4 h-4" />}
        >
          Buchungsjournal
        </TabButton>
      </div>

      {/* Inhalt */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <>
          {tab === 'uebersicht' && (
            <UebersichtTab ausgaben={ausgaben} belege={belege} journal={journal} />
          )}
          {tab === 'offene_punkte' && (
            <OffenePunkteTab ausgaben={ausgaben} belege={belege} />
          )}
          {tab === 'journal' && (
            <JournalTab journal={journal} firmaKey={firmaKey} />
          )}
        </>
      )}
    </div>
  );
}
